<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AgentAPI from 'dashboard/api/agents';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: { type: Object, required: true },
});
const emit = defineEmits(['close', 'done']);
const { t, te } = useI18n();
const store = useStore();
const dialog = ref(null);
const summary = ref(null);
const replacementId = ref('');
const loading = ref(false);
const submitting = ref(false);
const errorMessage = ref('');
const title = computed(() =>
  t('AGENT_LIFECYCLE.REMOVE.TITLE', { name: props.agent.name })
);
const canConfirm = computed(
  () =>
    !loading.value &&
    !submitting.value &&
    Boolean(summary.value && replacementId.value)
);

const showError = error => {
  const key = `AGENT_LIFECYCLE.ERRORS.${error.response?.data?.code}`;
  if (te(key)) {
    errorMessage.value = t(key);
  } else if ([401, 403].includes(error.response?.status)) {
    errorMessage.value = t('AGENT_LIFECYCLE.ERRORS.FORBIDDEN');
  } else if (error.response?.status === 404) {
    errorMessage.value = t('AGENT_LIFECYCLE.ERRORS.NOT_FOUND');
  } else {
    errorMessage.value = t('AGENT_LIFECYCLE.ERRORS.GENERIC');
  }
};
onMounted(async () => {
  dialog.value.open();
  loading.value = true;
  try {
    const response = await AgentAPI.getOffboarding(props.agent.id);
    summary.value = response.data;
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
});
const confirm = async () => {
  if (!canConfirm.value) return;
  submitting.value = true;
  errorMessage.value = '';
  try {
    await store.dispatch('agents/delete', {
      id: props.agent.id,
      replacementAgentId: Number(replacementId.value),
    });
    useAlert(t('AGENT_LIFECYCLE.REMOVE.SUCCESS'));
    emit('done', props.agent.id);
  } catch (error) {
    showError(error);
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialog"
    :title="title"
    overflow-y-auto
    @confirm="confirm"
    @close="emit('close')"
  >
    <div class="flex min-w-0 flex-col gap-4">
      <p class="m-0 text-sm leading-6 text-n-slate-12">
        {{ t('AGENT_LIFECYCLE.REMOVE.DESCRIPTION') }}
      </p>
      <p v-if="loading" class="m-0 text-sm text-n-slate-11" role="status">
        {{ t('AGENT_LIFECYCLE.LOADING') }}
      </p>
      <template v-if="summary">
        <dl
          class="m-0 grid grid-cols-1 gap-2 rounded-lg bg-n-alpha-2 p-3 text-sm"
        >
          <div
            v-for="item in ['ongoing_conversations', 'teams', 'inboxes']"
            :key="item"
            class="flex justify-between gap-4"
          >
            <dt>{{ t(`AGENT_LIFECYCLE.COUNTS.${item}`) }}</dt>
            <dd class="m-0 shrink-0 font-medium tabular-nums">
              {{ summary[item] }}
            </dd>
          </div>
        </dl>
        <label class="m-0 flex flex-col gap-2 text-sm text-n-slate-12">
          {{ t('AGENT_LIFECYCLE.REPLACEMENT_LABEL') }}
          <select
            v-model="replacementId"
            class="m-0 w-full"
            :disabled="submitting"
          >
            <option disabled value="">
              {{ t('AGENT_LIFECYCLE.REPLACEMENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="replacement in summary.replacements"
              :key="replacement.id"
              :value="replacement.id"
            >
              {{
                t('AGENT_LIFECYCLE.REPLACEMENT_OPTION', {
                  name: replacement.name,
                  email: replacement.email,
                })
              }}
            </option>
          </select>
        </label>
        <p
          v-if="!summary.replacements.length"
          class="m-0 text-sm text-n-ruby-11"
          role="alert"
        >
          {{ t('AGENT_LIFECYCLE.ERRORS.NO_REPLACEMENT') }}
        </p>
        <p class="m-0 text-xs leading-5 text-n-slate-11">
          {{
            t('AGENT_LIFECYCLE.PRESERVE_HISTORY', {
              count: summary.resolved_conversations,
            })
          }}
        </p>
      </template>
      <p v-if="errorMessage" class="m-0 text-sm text-n-ruby-11" role="alert">
        {{ errorMessage }}
      </p>
    </div>
    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          color="slate"
          variant="faded"
          class="w-full sm:w-auto"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          :disabled="submitting"
          @click="dialog.close()"
        />
        <Button
          type="button"
          class="w-full sm:w-auto"
          color="ruby"
          :label="t('AGENT_LIFECYCLE.REMOVE.CONFIRM')"
          :disabled="!canConfirm"
          :is-loading="submitting"
          @click="confirm"
        />
      </div>
    </template>
  </Dialog>
</template>
